import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import SnackbarContainer from '../components/ui/Snackbar';
import { connectSnackbar } from '../utils/notify';

const SnackbarContext = createContext(null);

let _nextId = 1;

/**
 * SnackbarProvider — wrap your app to enable useSnackbar() everywhere.
 *
 *   const { showSnackbar } = useSnackbar();
 *   showSnackbar('Saved!');                      // default: success, 3.5s
 *   showSnackbar('Oops', 'error');               // error, 4.5s
 *   showSnackbar('Note', 'info', { duration: 5000 });
 */
export const SnackbarProvider = ({ children, maxVisible = 4 }) => {
  const [snackbars, setSnackbars] = useState([]);

  const removeSnackbar = useCallback((id) => {
    setSnackbars((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const showSnackbar = useCallback(
    (message, variant = 'success', options = {}) => {
      const id = `snk-${_nextId++}`;
      const item = {
        id,
        message,
        variant,
        duration: options.duration ?? (variant === 'error' ? 4500 : 3500),
      };

      setSnackbars((prev) => {
        const next = [...prev, item];
        return next.length > maxVisible ? next.slice(-maxVisible) : next;
      });

      return id;
    },
    [maxVisible],
  );

  /* convenience shortcuts */
  const success = useCallback(
    (msg, opts) => showSnackbar(msg, 'success', opts),
    [showSnackbar],
  );
  const error = useCallback(
    (msg, opts) => showSnackbar(msg, 'error', opts),
    [showSnackbar],
  );
  const warning = useCallback(
    (msg, opts) => showSnackbar(msg, 'warning', opts),
    [showSnackbar],
  );
  const info = useCallback(
    (msg, opts) => showSnackbar(msg, 'info', opts),
    [showSnackbar],
  );

  // utils/notify (toast.success(...) etc.) shows its messages through this provider.
  useEffect(
    () => connectSnackbar({ showSnackbar, removeSnackbar }),
    [showSnackbar, removeSnackbar],
  );

  const value = { showSnackbar, success, error, warning, info, removeSnackbar };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <SnackbarContainer snackbars={snackbars} onClose={removeSnackbar} />
    </SnackbarContext.Provider>
  );
};

/**
 * useSnackbar()
 * @returns {{ showSnackbar, success, error, warning, info, removeSnackbar }}
 */
export const useSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error('useSnackbar must be used within a <SnackbarProvider>');
  }
  return ctx;
};

export default SnackbarContext;
